import React from "react";

interface InputFieldProps {
  id: string;
  label?: string;
  type?: string;
  value: string | number;
  // ⬇ keep the same signature your pages already use
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  error?: string;
  placeholder?: string;
  maxLength?: number;
  autoComplete?: string;
  inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal";
  pattern?: string;
  disabled?: boolean;
  title?: string;
  list?: string;

  /** Optional select support */
  as?: "input" | "select";
  options?: { value: string | number; label: string }[];

  // optional forwarded props
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  icon,
  error,
  placeholder,
  maxLength,
  autoComplete,
  inputMode,
  pattern,
  disabled = false,
  title,
  as = "input",
  options,
  onBlur,
  className,
  list,
}) => {
  // internal wrapper that safely forwards event.target to existing onChange
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    // cast so the external handler sees HTMLInputElement
    onChange(e as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <div className="relative">
      {as === "select" ? (
        <select
          id={id}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          title={title}
          className={`peer w-full border-0 border-b-2 bg-transparent text-gray-900
      focus:border-teal-500 focus:ring-0 focus:outline-none px-0 py-2 ${
        error ? "border-red-500" : "border-gray-300"
      } ${className ?? ""}`}
          // Only pass onBlur if it matches the select signature
          onBlur={undefined}
        >
          {/* 👇 default prompt */}
          <option value="" disabled hidden>
            Select {label}
          </option>

          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          id={id}
          value={value ?? ""}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder || label}
          maxLength={maxLength}
          autoComplete={autoComplete || "off"}
          inputMode={inputMode}
          pattern={pattern}
          disabled={disabled}
          title={title}
          list={list}
          className={`peer w-full border-0 border-b-2 bg-transparent text-gray-900 placeholder-transparent
            focus:border-teal-500 focus:ring-0 focus:outline-none px-0 py-2 ${
              error ? "border-red-500" : "border-gray-300"
            } ${className ?? ""}`}
        />
      )}

      <label
        htmlFor={id}
        className="absolute left-0 -top-3.5 flex items-center gap-1 text-gray-500 peer-focus:text-teal-500 text-sm transition-all
          peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
          peer-placeholder-shown:top-2 peer-focus:-top-3.5 peer-focus:text-sm"
      >
        {icon}
        {label}
      </label>

      {error && (
        <p className="flex items-center mt-1 text-red-500 text-sm">{error}</p>
      )}
    </div>
  );
};

export default InputField;
